import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../services/firebaseConnection";
import { useAuth } from "../../context/AuthContext";
import ShinyText from "../../components/ShinyText";
import Particles from "../../components/Particles";
import TextType from "../../components/TextType";
import logoEmpresa from "../../assets/img/logo.png";
import showPassword from "../../assets/icons/eye.png";
import hiddenPassword from "../../assets/icons/hidden.png";
import "./home.css";

const Home = () => {
  const [password, setPassword] = useState("");
  const [showPasswordIcon, setShowPasswordIcon] = useState(showPassword);
  const [typePassword, setTypePassword] = useState("password");

  const [administradores, setAdministradores] = useState([]);
  const [nomeUsuario, setNomeUsuario] = useState("");
  const [usuarioSelecionado, setUsuarioSelecionado] = useState(null);
  const [mostrarSugestoes, setMostrarSugestoes] = useState(false);

  const navigate = useNavigate();
  const { login, logout, usuarioLogado } = useAuth();

  const usuarioJaLogado = Boolean(usuarioLogado);

  useEffect(() => {
    async function buscarAdministradores() {
      try {
        const administradoresRef = collection(db, "administradores");
        const snapshot = await getDocs(administradoresRef);

        const lista = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setAdministradores(lista);
      } catch (error) {
        console.error("Erro ao buscar administradores:", error);
        alert("Erro ao carregar usuários!");
      }
    }

    buscarAdministradores();
  }, []);

  useEffect(() => {
    if (usuarioLogado?.nome) {
      setNomeUsuario(usuarioLogado.nome);
    }
  }, [usuarioLogado]);

  const sugestoesFiltradas = useMemo(() => {
    const texto = nomeUsuario.trim().toLowerCase();

    if (!texto || usuarioJaLogado) return [];

    return administradores.filter((admin) =>
      admin.nome?.toLowerCase().includes(texto),
    );
  }, [nomeUsuario, administradores, usuarioJaLogado]);

  function handleInputChange(event) {
    const inputValue = event.target.value.replace(/\D/g, "");

    if (inputValue.length <= 4) {
      setPassword(inputValue);
    }
  }

  function handleNomeChange(event) {
    const valor = event.target.value.toUpperCase();

    setNomeUsuario(valor);
    setMostrarSugestoes(true);

    if (
      usuarioSelecionado &&
      valor.trim().toLowerCase() !== usuarioSelecionado.nome?.toLowerCase()
    ) {
      setUsuarioSelecionado(null);
    }
  }

  function selecionarUsuario(usuario) {
    setNomeUsuario(usuario.nome);
    setUsuarioSelecionado(usuario);
    setMostrarSugestoes(false);
  }

  function changeImage() {
    setShowPasswordIcon((prevIcon) =>
      prevIcon === showPassword ? hiddenPassword : showPassword,
    );

    setTypePassword((prevType) =>
      prevType === "password" ? "text" : "password",
    );
  }

  function acessarSistema() {
    if (usuarioJaLogado) {
      navigate("/main", { replace: true });
      return;
    }

    if (!usuarioSelecionado) {
      alert("Selecione um usuário da lista!");
      return;
    }

    if (password.length < 4) {
      alert("Digite os 4 primeiros dígitos do CPF!");
      return;
    }

    const primeirosQuatroDigitos = usuarioSelecionado.cpf?.slice(0, 4);

    if (password !== primeirosQuatroDigitos) {
      alert("Senha incorreta!");
      return;
    }

    login({
      id: usuarioSelecionado.id,
      nome: usuarioSelecionado.nome,
      cpf: usuarioSelecionado.cpf,
      sexo: usuarioSelecionado.sexo,
      modificar: usuarioSelecionado.modificar,
    });

    navigate("/main", { replace: true });
  }

  function deslogarSistema() {
    logout();

    setPassword("");
    setNomeUsuario("");
    setUsuarioSelecionado(null);
    setMostrarSugestoes(false);
  }

  function handleKeyDown(event) {
    if (event.key === "Enter" && !usuarioJaLogado) {
      acessarSistema();
    }
  }

  const primeiroNome =
    usuarioLogado?.nome?.split(" ")[0] || nomeUsuario?.split(" ")[0] || "";

  return (
    <div className="home">
      <div className="particles-bg">
        <Particles
          particleColors={["#ffffff"]}
          particleCount={75}
          particleSpread={7}
          speed={0.045}
          particleBaseSize={70}
          moveParticlesOnHover
          alphaParticles
          disableRotation={false}
          pixelRatio={1}
        />
      </div>

      <div className="header">
        <h1>
          <ShinyText
            text="CADASTRO DE TREINAMENTO"
            speed={2}
            color="#babfc2"
            shineColor="#f0f9fd"
            spread={160}
            direction="left"
          />
        </h1>

        <div className="input-container">
          <div className="input-search-user">
            <div className="input-main-pass">
              <input
                type="text"
                placeholder="PESQUISE SEU NOME"
                value={nomeUsuario}
                onChange={handleNomeChange}
                onFocus={() => setMostrarSugestoes(true)}
                onKeyDown={handleKeyDown}
                autoComplete="off"
                disabled={usuarioJaLogado}
              />
            </div>

            {mostrarSugestoes && sugestoesFiltradas.length > 0 && (
              <div className="lista-sugestoes">
                {sugestoesFiltradas.map((admin) => (
                  <div
                    key={admin.id}
                    className="item-sugestao"
                    onMouseDown={() => selecionarUsuario(admin)}
                  >
                    {admin.nome}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="input-main-pass">
            <input
              type={typePassword}
              placeholder="SENHA"
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              maxLength={4}
              value={usuarioJaLogado ? "****" : password}
              disabled={usuarioJaLogado}
            />

            {!usuarioJaLogado && (
              <img
                onClick={changeImage}
                src={showPasswordIcon}
                alt="Mostrar ou ocultar senha"
              />
            )}
          </div>

          {!usuarioJaLogado ? (
            <button
              type="button"
              className="btn-entrar"
              onClick={acessarSistema}
            >
              ENTRAR
            </button>
          ) : (
            <button
              type="button"
              className="btn-deslogar"
              onClick={deslogarSistema}
            >
              DESLOGAR DO SISTEMA
            </button>
          )}
        </div>

        {usuarioJaLogado && (
          <button
            type="button"
            className="btn-continuar"
            onClick={acessarSistema}
          >
            ACESSAR SISTEMA
          </button>
        )}

        <div className="logo-login">
          <img src={logoEmpresa} alt="Logo da empresa" />
        </div>
      </div>

      <p className="mensagem-colaborador">
        <TextType
          text={[
            `SUA COLABORAÇÃO AQUI É IMPORTANTE PRA NÓS, ${
              primeiroNome ? primeiroNome.toUpperCase() : "COLABORADOR"
            }!`,
            "MANTENHA OS TREINAMENTOS SEMPRE ATUALIZADOS!",
            "A ATLAS ENGENHARIA AGRADECE SEU APOIO!",
            "OBRIGADO POR COLABORAR COM A GESTÃO DE TREINAMENTOS!",
          ]}
          typingSpeed={150}
          pauseDuration={2200}
          showCursor
          cursorCharacter="_"
          texts={[
            "Mantenha seus treinamentos sempre atualizados!",

            "Obrigado por colaborar com a gestão de treinamentos!",
          ]}
          deletingSpeed={50}
          variableSpeedEnabled={false}
          variableSpeedMin={60}
          variableSpeedMax={120}
          cursorBlinkDuration={0.5}
        />
      </p>
    </div>
  );
};

export default Home;
